import moment from 'moment';
import React from 'react';

import LocaleService from '@services/LocaleService';
import { CustomButton } from '@shared/buttons';
import { formatPhoneNumber } from '@shared/helpers';
import {
  CheckIcon, EmailIcon, PhoneIcon, StethoscopeIcon,
} from '@shared/icons';

type Props = {
  result: {
    reference_no: string;
    submitted_at: string;
    clinic: { name: string; email?: string; phone_number?: string | null };
    // ENH-056: the dentist the guest picked at the clinic, if any.
    dentist?: { full_name: string } | null;
    register_url: string;
  };
};

const i18n = LocaleService.getTranslations('referrals.guestRefer.submitted') as any;

/**
 * ENH-052 (plan §3.3, design 03): what the guest sees once the referral is in,
 * the same content as their confirmation email. No patient information.
 */
const GuestReferralSubmitted = ({ result }: Props) => {
  const { clinic, dentist } = result;
  // The register link is absolute (it is also in the email); stay on this site.
  const registerPath = (() => {
    try {
      const url = new URL(result.register_url);
      return `${url.pathname}${url.search}`;
    } catch {
      return '/auth/register';
    }
  })();

  return (
    <div className="guest-refer-submitted">
      <div className="guest-refer-submitted-body">
        <h1 className="guest-refer-submitted-headline">{i18n.headline}</h1>
        <p className="guest-refer-submitted-subheadline">
          {dentist
            ? LocaleService.parseTranslation(i18n.subheadlineDentist, { ':dentist': dentist.full_name, ':clinic': clinic.name })
            : LocaleService.parseTranslation(i18n.subheadline, { ':clinic': clinic.name })}
        </p>
        <div className="guest-refer-submitted-clinic">
          <span className="guest-refer-submitted-clinic-mark">
            <StethoscopeIcon width={24} height={24} />
          </span>
          <div className="guest-refer-submitted-clinic-details">
            <div className="guest-refer-submitted-clinic-name">{clinic.name}</div>
            {clinic.phone_number && (
              <div className="guest-refer-submitted-contact">
                <PhoneIcon />
                <span>{formatPhoneNumber(clinic.phone_number)}</span>
              </div>
            )}
            {clinic.email && (
              <div className="guest-refer-submitted-contact">
                <EmailIcon />
                <span>{clinic.email}</span>
              </div>
            )}
          </div>
          <div className="guest-refer-submitted-meta">
            <div className="guest-refer-submitted-date">
              {moment(result.submitted_at).format('MMM D, YYYY')}
            </div>
            <div className="guest-refer-submitted-reference">
              {LocaleService.parseTranslation(i18n.reference, { ':reference': result.reference_no })}
            </div>
          </div>
        </div>
      </div>
      <div className="guest-refer-submitted-track">
        <h2 className="guest-refer-submitted-track-title">{i18n.trackTitle}</h2>
        <p className="guest-refer-submitted-track-text">
          {LocaleService.parseTranslation(i18n.trackText, { ':clinic': clinic.name })}
        </p>
        <ul className="guest-refer-submitted-benefits">
          {i18n.benefits.map((benefit: string) => (
            <li key={benefit}>
              <span className="guest-refer-submitted-benefit-mark">
                <CheckIcon width={11} height={8} fill="#FFFFFF" />
              </span>
              {benefit}
            </li>
          ))}
        </ul>
        <CustomButton
          id="guest-refer-create-account"
          className="btn-lg guest-refer-submitted-cta"
          color="primary"
          onClick={() => { window.location.href = registerPath; }}
        >
          {i18n.createAccount}&nbsp;&rarr;
        </CustomButton>
      </div>
    </div>
  );
};

export default GuestReferralSubmitted;
